// meetingRooms/view.js - Updated with direct save for GeneralInfo
import React, { useEffect } from 'react';
import {
  Alert,
  Snackbar,
  CircularProgress,
  Box,
  Typography,
  Collapse,
  Divider,
  Fade
} from '@mui/material';
import {
  Save as SaveIcon,
  People as PeopleIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Image as ImageIcon,
  MeetingRoom as MeetingRoomIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { API_BASE_URL } from 'config/api';
import GeneralInfo from '../components/generalInfo';
import ServicesTab from '../components/services';
import GalleryTab from '../components/gallery';
import PricingTab from '../components/meetingRooms/prices';
import ResourceDialog from '../../dialogs/ResourceDialog/index';
import {
  StyledContainer,
  StyledHeader,
  StyledTitle,
  StyledWorkAreaDetails,
  StyledSectionContent,
  StyledLoadingContainer,
  StyledEmptyState,
  StyledFloatingSaveContainer,
  StyledFloatingSaveWrapper,
  StyledFloatingSaveCancelButton,
  StyledFloatingSaveButton,
  RoomPaper,
  RoomHeaderBox,
  RoomImageContainer,
  RoomContentContainer,
  RoomTitle,
  RoomSubtitle,
  CapacityChip,
  ExpandButton,
  ServicesContainer,
  ServiceChip,
  PlaceholderIconStyled,
  RoomImage,
  AddMeetingRoomCard,
  AddMeetingRoomContent,
  AddMeetingRoomIcon,
  AddMeetingRoomText,
  AddMeetingRoomSubtext
} from './styled';

// Helper to get image URL, handling both absolute and relative paths
const getImageUrl = (imagePath) => {
  if (!imagePath) return null;
  return imagePath.startsWith('http') ? imagePath : `${API_BASE_URL}${imagePath}`;
};

const MeetingRoomsView = ({
  // Data
  meetingRooms,
  meetingRoomResourceType,
  workAreas,
  services,
  venueInfo,
  parentIsLoading,

  // Meeting Room State
  expandedMeetingRooms,
  showCreateDialog,

  // GeneralInfo props
  generalInfoFormData,
  generalInfoHasChanges,
  isSavingGeneralInfo,

  // Services props
  servicesFormData,
  servicesHasChanges,
  isSavingServices,

  // Gallery props
  galleryFormData,
  newImages,
  isSavingGallery,

  // Pricing props
  pricingFormData,
  pricingHasChanges,
  isSavingPricing,
  isLoadingTariffs,
  showNewTariffForm,

  // Snackbar
  snackbar,

  // Computed values
  hasMeetingRooms,
  totalRooms,
  VAT_RATE,

  // Handlers
  handleToggleMeetingRoom,
  handleCloseCreateDialog,
  handleOpenCreateDialog,
  handleCreateMeetingRoom,
  showSnackbar,
  handleCloseSnackbar,

  // GeneralInfo handlers
  handleGeneralInfoFormChange,
  handleSaveGeneralInfo,
  handleCancelGeneralInfo,

  // Services handlers
  handleServicesFormChange,
  handleSaveServices,
  handleCancelServices,
  handleServiceMoveUp,
  handleServiceMoveDown,
  handleServiceDescriptionSave,
  handleAddService,
  handleRemoveService,

  // Gallery handlers
  handleSaveGallery,
  handleCancelGallery,
  handleImageUpload,
  handleImageDelete,
  handleImageMoveLeft,
  handleImageMoveRight,

  // Pricing handlers (from usePricing hook)
  handleCreateTariff,
  handleUpdateTariff,
  handleDeleteTariff,
  handleToggleNewTariffForm,
  handleSavePricing,
  handleCancelPricing,
  fetchTariffs,

  // External functions
  getWorkAreaName,
  getTariffsForResource,
  handleDeleteResource,

  // API functions
  updateResourceManagement
}) => {

  // Auto-expand the only meeting room if there is just one and it's not already expanded
  useEffect(() => {
    if (meetingRooms && meetingRooms.length === 1) {
      const onlySlug = meetingRooms[0].slug;
      if (!expandedMeetingRooms[onlySlug]) {
        handleToggleMeetingRoom(onlySlug);
      }
    }
  }, [meetingRooms, expandedMeetingRooms, handleToggleMeetingRoom]);

  // Safety check for critical props - AFTER all hooks
  if (!meetingRooms) {
    console.error('❌ meetingRooms is undefined!');
    return (
      <StyledContainer>
        <StyledHeader>
          <StyledTitle>Error: Meeting rooms data not available</StyledTitle>
        </StyledHeader>
      </StyledContainer>
    );
  }

  // Add Meeting Room Card component
  const renderAddMeetingRoomCard = () => (
    <AddMeetingRoomCard onClick={() => {
      if (handleOpenCreateDialog) {
        handleOpenCreateDialog();
      } else {
        showSnackbar('Create dialog function not available', 'error');
      }
    }}>
      <AddMeetingRoomContent>
        <AddMeetingRoomIcon>
          <AddIcon />
        </AddMeetingRoomIcon>
        <AddMeetingRoomText>
          Add Meeting Room
        </AddMeetingRoomText>
        <AddMeetingRoomSubtext>
          Create a new meeting room with custom capacity and features
        </AddMeetingRoomSubtext>
      </AddMeetingRoomContent>
    </AddMeetingRoomCard>
  );

  const renderLoadingState = () => (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>
          Loading meeting rooms...
        </StyledTitle>
      </StyledHeader>
    </StyledContainer>
  );

  const renderNoVenueInfo = () => (
    <StyledContainer>
      <StyledHeader>
        <StyledTitle>
          Venue information not available
        </StyledTitle>
      </StyledHeader>
    </StyledContainer>
  );

  const renderNoMeetingRoomsState = () => (
    <Box>
      <StyledEmptyState severity="info" sx={{ mb: 2 }}>
        No meeting rooms have been added yet. Click "Add Meeting Room" to create your first one.
      </StyledEmptyState>
      {/* Always show the add button, even when no rooms */}
      {renderAddMeetingRoomCard()}
      {renderResourceDialog()}
      {renderSnackbar()}
    </Box>
  );

  const renderGeneralInfoSection = (room) => {
    const formData = generalInfoFormData[room.slug] || {
      name: '',
      description: '',
      capacity: 1,
      status: 'IN',
      slug: room.slug,
      canBeActive: true
    };

    return (
      <StyledSectionContent>
        <GeneralInfo
          target={{
            type: 'meetingRoom',
            slug: room.slug
          }}
          formData={formData}
          onFormChange={(newFormData) =>
            handleGeneralInfoFormChange(room.slug, newFormData)
          }
          onSave={async (formData) => {
            await handleSaveGeneralInfo(room.slug);
          }}
          onCancel={() => handleCancelGeneralInfo(room.slug)}
          canActivate={formData.canBeActive}
          hasChanges={generalInfoHasChanges[room.slug] || false}
          isSaving={isSavingGeneralInfo}
        />
      </StyledSectionContent>
    );
  };

  const renderFloatingSaveButton = (room) => {
    const hasServicesChanges = servicesHasChanges[room.slug];
    const hasNewImages = newImages[room.slug] && newImages[room.slug].length > 0;

    // Now we only show floating save for Services and Gallery (GeneralInfo has its own save button)
    const hasChanges = hasServicesChanges || hasNewImages;
    const isSaving = isSavingServices || isSavingGallery;

    if (!hasChanges && !isSaving) return null;

    const handleSaveAll = async () => {
      try {
        if (hasServicesChanges) {
          await handleSaveServices(room.slug);
        }
        if (hasNewImages) {
          await handleSaveGallery(room.slug);
        }
      } catch (error) {
        console.error('Error saving changes:', error);
      }
    };

    const handleCancelAll = () => {
      if (hasServicesChanges) {
        handleCancelServices(room.slug);
      }
      if (hasNewImages) {
        handleCancelGallery(room.slug);
      }
    };
    
    return (
      <StyledFloatingSaveContainer>
        <Fade in={!isSaving}>
          <StyledFloatingSaveWrapper>
            <StyledFloatingSaveCancelButton
              variant="outlined"
              onClick={handleCancelAll}
            >
              Cancel
            </StyledFloatingSaveCancelButton>

            <StyledFloatingSaveButton
              variant="contained"
              size="large"
              startIcon={<SaveIcon />}
              onClick={handleSaveAll}
            >
              Save Changes
            </StyledFloatingSaveButton>
          </StyledFloatingSaveWrapper>
        </Fade>

        <Fade in={isSaving}>
          <StyledFloatingSaveWrapper>
            <StyledFloatingSaveButton
              variant="contained"
              size="large"
              disabled
              startIcon={<CircularProgress size={20} sx={{ color: 'white' }} />}
            >
              Saving...
            </StyledFloatingSaveButton>
          </StyledFloatingSaveWrapper>
        </Fade>
      </StyledFloatingSaveContainer>
    );
  };

  // Services section implementation
  const renderServicesSection = (room) => {
    const services = servicesFormData[room.slug] || [];

    return (
      <StyledSectionContent>
        <ServicesTab
          selectedServices={services}
          onServicesChange={(newServices) => handleServicesFormChange(room.slug, newServices)}
          onServiceMoveUp={(serviceSlug) => handleServiceMoveUp(room.slug, serviceSlug)}
          onServiceMoveDown={(serviceSlug) => handleServiceMoveDown(room.slug, serviceSlug)}
          onServiceDescriptionSave={(serviceSlug, description) => handleServiceDescriptionSave(room.slug, serviceSlug, description)}
          onAddService={(service) => handleAddService(room.slug, service)}
          onRemoveService={(serviceSlug) => handleRemoveService(room.slug, serviceSlug)}
          showSnackbar={showSnackbar}
          disableSaveButton={true}
          isSaving={isSavingServices}
        />
      </StyledSectionContent>
    );
  };

  // Gallery section implementation
  const renderGallerySection = (room) => {
    const images = galleryFormData[room.slug] || [];
    const newImagesData = newImages[room.slug] || [];

    return (
      <StyledSectionContent>
        <GalleryTab
          orderedImages={images}
          orderedNewImages={newImagesData}
          onImagesChange={() => { }} // No longer needed - operations are immediate
          onImageUpload={(files) => handleImageUpload(room.slug, files)}
          onImageDelete={(index, isNew) => handleImageDelete(room.slug, index, isNew)}
          onImageMoveLeft={(index, isNew) => handleImageMoveLeft(room.slug, index, isNew)}
          onImageMoveRight={(index, isNew) => handleImageMoveRight(room.slug, index, isNew)}
          showSnackbar={showSnackbar}
          disableSaveButton={true} // Gallery operations are immediate
        />
      </StyledSectionContent>
    );
  };

  // Pricing section implementation - Use ONLY hook functions
  const renderPricingSection = (room) => {
    const tariffs = pricingFormData[room.slug] || [];
    const isLoadingTariffsForRoom = isLoadingTariffs[room.slug] || false;
    const showNewTariffFormForRoom = showNewTariffForm[room.slug] || false;

    return (
      <StyledSectionContent>
        <PricingTab
          resource={room}
          tariffs={tariffs}
          isLoadingTariffs={isLoadingTariffsForRoom}
          showNewTariffForm={showNewTariffFormForRoom}
          isSavingPricing={isSavingPricing}
          // Use hook functions only - These do optimistic updates
          onUpdateTariff={(resourceSlug, tariffSlug, tariffData) => {
            handleUpdateTariff(resourceSlug, tariffSlug, tariffData)
          }}
          onToggleNewTariffForm={() => handleToggleNewTariffForm(room.slug)}
          venueInfo={venueInfo}
          showSnackbar={showSnackbar}
          disableSaveButton={true} // Pricing operations are immediate
        />
      </StyledSectionContent>
    );
  };

  const renderMeetingRoomContent = (room) => (
    <StyledWorkAreaDetails>
      {renderGeneralInfoSection(room)}

      <Divider sx={{ my: 3 }} />
      {renderServicesSection(room)}

      <Divider sx={{ my: 3 }} />
      {renderGallerySection(room)}

      <Divider sx={{ my: 3 }} />
      {renderPricingSection(room)}

      {/* Floating save button now only for Services and Gallery */}
      {renderFloatingSaveButton(room)}
    </StyledWorkAreaDetails>
  );

  const renderMeetingRoomCard = (room) => {
    if (!room) {
      console.error('❌ Attempted to render undefined room');
      return null;
    }
    
    const isExpanded = !!expandedMeetingRooms[room.slug];
    const firstImage = room.images && room.images.length > 0 ? getImageUrl(room.images[0].url) : null;
    const roomServices = room.services || [];

    return (
      <RoomPaper
        key={room.slug}
        isExpanded={isExpanded}
      >
        {/* Enhanced header with image and content */}
        <RoomHeaderBox
          isExpanded={isExpanded}
          onClick={() => handleToggleMeetingRoom(room.slug)}
        >
          {/* Image container */}
          <RoomImageContainer>
            {firstImage ? (
              <RoomImage
                src={firstImage}
                alt={room.name}
                onError={(e) => {
                  // Fallback to placeholder if image fails to load
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
            ) : null}
            {!firstImage && (
              <PlaceholderIconStyled>
                <MeetingRoomIcon />
              </PlaceholderIconStyled>
            )}
            {/* Hidden fallback for failed image loads */}
            {firstImage && (
              <PlaceholderIconStyled style={{ display: 'none' }}>
                <MeetingRoomIcon />
              </PlaceholderIconStyled>
            )}
          </RoomImageContainer>

          {/* Content container */}
          <RoomContentContainer>
            {/* Room title */}
            <RoomTitle variant="h6">
              {room.name}
            </RoomTitle>

            {/* Capacity chip */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <CapacityChip
                icon={<PeopleIcon />}
                label={`Capacity: ${room.capacity}`}
                size="small"
              />
            </Box>

            {/* Services (show first 3) */}
            {roomServices.length > 0 && (
              <ServicesContainer>
                {roomServices.slice(0, 3).map((service) => (
                  <ServiceChip
                    key={service.slug}
                    label={service.name}
                    size="small"
                  />
                ))}
                {roomServices.length > 3 && (
                  <ServiceChip
                    label={`+${roomServices.length - 3} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </ServicesContainer>
            )}

            {/* Expand button */}
            <ExpandButton isExpanded={isExpanded}>
              <KeyboardArrowDownIcon />
            </ExpandButton>
          </RoomContentContainer>
        </RoomHeaderBox>

        {/* Collapsible content */}
        <Collapse in={isExpanded} timeout={300}>
          <Box
            sx={{ p: 3, pt: 0 }}
            onClick={(e) => e.stopPropagation()} // Prevent event bubbling
          >
            {renderMeetingRoomContent(room)}
          </Box>
        </Collapse>
      </RoomPaper>
    );
  };

  // Updated to include the add button
  const renderMeetingRoomsList = () => (
    parentIsLoading ? (
      <StyledLoadingContainer>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading meeting rooms...</Typography>
      </StyledLoadingContainer>
    ) : (
      <>
        {meetingRooms.length === 0 ? (
          <StyledEmptyState severity="info" sx={{ mb: 2 }}>
            No meeting rooms have been added yet. Click "Add Meeting Room" to create your first one.
          </StyledEmptyState>
        ) : (
          meetingRooms.map(renderMeetingRoomCard)
        )}
        {/* Add Meeting Room button - always show this when we have rooms */}
        {meetingRooms.length > 0 && renderAddMeetingRoomCard()}
      </>
    )
  );

  const renderResourceDialog = () => (
    <ResourceDialog
      open={showCreateDialog}
      onClose={handleCloseCreateDialog}
      onSubmit={handleCreateMeetingRoom}
      resource={{
        name: '',
        resource_type: 'MR',
        work_area: (workAreas && workAreas.length > 0) ? workAreas[0].id : null,
        capacity: 4,
      }}
      workAreas={workAreas || []}
      resourceTypes={meetingRoomResourceType ? [meetingRoomResourceType] : []}
      isEditing={false}
    />
  );

  const renderSnackbar = () => (
    <Snackbar
      open={snackbar.open}
      autoHideDuration={6000}
      onClose={handleCloseSnackbar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    >
      <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
      >
        {snackbar.message}
      </Alert>
    </Snackbar>
  );

  const renderMainMeetingRoomsView = () => (
    <Box>
      {renderMeetingRoomsList()}
      {renderResourceDialog()}
      {renderSnackbar()}
    </Box>
  );

  // Main render logic - following WorkAreas pattern
  if (parentIsLoading) {
    return renderLoadingState();
  }

  if (!venueInfo) {
    return renderNoVenueInfo();
  }

  if (!hasMeetingRooms) {
    return renderNoMeetingRoomsState();
  }

  return renderMainMeetingRoomsView();
};

export default MeetingRoomsView;